import { TutorialStepDetails, TutorialStepId } from './tutorial.models';

export const TUTORIAL_STEP_DETAILS: Record<TutorialStepId, TutorialStepDetails> = {
  'labs-start': {
    title: 'Create your first lab',
    body: 'Click Get Started to open the lab creation flow. In b.Board, a lab is the shared home for your people, gadgets, and settings.',
    progressLabel: 'Step 1 of 14',
    targetSelector: '[data-tutorial-target="labs-get-started"]',
    sheetPosition: 'bottom',
  },
  'new-lab-create-intro': {
    title: 'Create starts a fresh lab',
    body: 'This tab makes a brand new Brilliant Labs workspace for you and your team. A lab is the top-level shared space where your gadgets, components, and members live.',
    progressLabel: 'Step 2 of 14',
    targetSelector: '[data-tutorial-target="new-lab-create-tab"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'new-lab-join-intro': {
    title: 'Join uses a quick join code',
    body: 'Join is for entering an existing lab, which means joining someone else\'s shared workspace with a quick join code they send you.',
    progressLabel: 'Step 3 of 14',
    targetSelector: '[data-tutorial-target="new-lab-join-tab"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'new-lab-clone-intro': {
    title: 'Clone copies an existing lab setup',
    body: 'Clone lets you duplicate a starter lab instead of building everything from scratch.',
    progressLabel: 'Step 4 of 14',
    targetSelector: '[data-tutorial-target="new-lab-clone-tab"]',
    actionLabel: 'Back to Create',
    sheetPosition: 'bottom',
  },
  'create-lab-name': {
    title: 'Name your lab',
    body: 'Type whatever lab name you want here, then click Next when you are ready to continue.',
    progressLabel: 'Step 5 of 14',
    targetSelector: '[data-tutorial-target="create-lab-name"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'create-lab-open-icon-selector': {
    title: 'Open the icon picker',
    body: 'Click the current lab image so we can choose an icon for this Brilliant Labs build.',
    progressLabel: 'Step 6 of 14',
    targetSelector: '[data-tutorial-target="create-lab-icon-preview"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'create-lab-upload-custom': {
    title: 'You can upload your own icon',
    body: 'This upload button lets you use your own image later. Do not tap it in this tutorial step, just use Next so we stay on the guided Brilliant Labs path.',
    progressLabel: 'Step 7 of 14',
    targetSelector: '[data-tutorial-target="create-lab-upload-custom"]',
    actionLabel: 'Next: choose a built-in icon',
    allowTargetInteraction: false,
    sheetPosition: 'bottom',
  },
  'create-lab-select-curated': {
    title: 'Pick one of our icons',
    body: 'Choose any of the built-in icons to continue the tutorial.',
    progressLabel: 'Step 8 of 14',
    targetSelector: '[data-tutorial-target="create-lab-curated-icons"]',
    sheetPosition: 'top',
  },
  'create-lab-finish': {
    title: 'Finish creating your lab',
    body: 'Tap Finish to create the lab.',
    progressLabel: 'Step 9 of 14',
    targetSelector: '[data-tutorial-target="create-lab-finish"]',
    sheetPosition: 'top',
  },
  'lab-settings-open': {
    title: 'Open Lab Settings',
    body: 'Tap Lab Settings so we can show where this lab can be edited later.',
    progressLabel: 'Step 10 of 14',
    targetSelector: '[data-tutorial-target="dashboard-lab-settings-trigger"]',
    sheetPosition: 'bottom',
  },
  'lab-settings-edit-info': {
    title: 'You can edit lab information here',
    body: 'Lab Settings lets you update the lab name, access, and icon after creation.',
    progressLabel: 'Step 11 of 14',
    targetSelector: '[data-tutorial-target="lab-settings-name"]',
    actionLabel: 'Next: quick join code',
    sheetPosition: 'bottom',
  },
  'lab-settings-quick-join-code': {
    title: 'Quick join is how others can enter this lab',
    body: 'This text box shows the current quick join code you can share when you want someone to enter this Brilliant Labs lab.',
    progressLabel: 'Step 12 of 14',
    targetSelector: '[data-tutorial-target="lab-settings-quick-join-code"]',
    actionLabel: 'Next: copy code',
    sheetPosition: 'top',
  },
  'lab-settings-quick-join-copy': {
    title: 'Copy the quick join code',
    body: 'Use this copy button when you want to send the current code to someone quickly.',
    progressLabel: 'Step 13 of 14',
    targetSelector: '[data-tutorial-target="lab-settings-quick-join-copy"]',
    actionLabel: 'Next: refresh code',
    sheetPosition: 'top',
  },
  'lab-settings-quick-join-refresh': {
    title: 'Refresh the quick join code when needed',
    body: 'Use refresh to generate a new quick join code. That is useful if you want to stop people who still have the current code from getting back into the lab.',
    progressLabel: 'Step 14 of 14',
    targetSelector: '[data-tutorial-target="lab-settings-quick-join-refresh"]',
    actionLabel: 'Done',
    sheetPosition: 'top',
  },
  'lab-members-help': {
    title: 'Learn how lab roles work',
    body: 'Open the role guide so Owners, Admins, Creators, and Viewers are explained in one place.',
    progressLabel: 'Step 1 of 2',
    targetSelector: '[data-tutorial-target="lab-members-role-help"]',
    sheetPosition: 'bottom',
  },
  'lab-members-role-help': {
    title: 'Read the role guide',
    body: 'This whole role guide stays visible during the inline tutorial. Scroll through it to see what Owners, Admins, Creators, and Viewers can do, then close the role guide when you are ready.',
    progressLabel: 'Step 2 of 2',
    targetSelector: '[data-tutorial-target="role-help-dialog"]',
    actionLabel: 'Close role guide',
    sheetPosition: 'bottom',
  },
  'lab-gadgets-start': {
    title: 'Create your first gadget',
    body: 'Open the Create Gadget card so we can add the first build inside this Brilliant Labs lab. A gadget is the overall device or project you are building.',
    progressLabel: 'Step 1 of 7',
    targetSelector: '[data-tutorial-target="lab-create-gadget"]',
    sheetPosition: 'bottom',
  },
  'create-gadget-name': {
    title: 'Name your gadget',
    body: 'Type whatever gadget name you want here, then click Next when you are ready to continue.',
    progressLabel: 'Step 2 of 7',
    targetSelector: '[data-tutorial-target="create-gadget-name"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'create-gadget-open-icon-selector': {
    title: 'Open the gadget icon picker',
    body: 'Click the current gadget image so we can choose an icon for this Brilliant Labs build.',
    progressLabel: 'Step 3 of 7',
    targetSelector: '[data-tutorial-target="create-gadget-icon-preview"]',
    sheetPosition: 'bottom',
  },
  'create-gadget-upload-custom': {
    title: 'You can upload your own gadget icon',
    body: 'This upload button lets you use your own image later. Do not tap it in this tutorial step, just use Next so we stay on the guided Brilliant Labs path.',
    progressLabel: 'Step 4 of 7',
    targetSelector: '[data-tutorial-target="create-gadget-upload-custom"]',
    actionLabel: 'Next: choose a built-in icon',
    allowTargetInteraction: false,
    sheetPosition: 'bottom',
  },
  'create-gadget-select-curated': {
    title: 'Pick one of our gadget icons',
    body: 'Choose any of the built-in icons to continue the tutorial.',
    progressLabel: 'Step 5 of 7',
    targetSelector: '[data-tutorial-target="create-gadget-curated-icons"]',
    sheetPosition: 'top',
  },
  'create-gadget-finish': {
    title: 'Finish creating your gadget',
    body: 'Tap Finish to create the gadget.',
    progressLabel: 'Step 6 of 7',
    targetSelector: '[data-tutorial-target="create-gadget-finish"]',
    sheetPosition: 'top',
  },
  'gadget-settings-gear': {
    title: 'This gear opens Gadget Settings',
    body: 'Use Gadget Settings any time you want to change the overall gadget information later, like the device name or icon.',
    progressLabel: 'Step 7 of 7',
    targetSelector: '[data-tutorial-target="gadget-settings-gear"]',
    actionLabel: 'Done',
    sheetPosition: 'bottom',
  },
  'gadget-users-help': {
    title: 'Learn how gadget roles work',
    body: 'Open the role guide so it is clear who can manage gadget members and who can build inside this gadget.',
    progressLabel: 'Step 1 of 2',
    targetSelector: '[data-tutorial-target="gadget-users-role-help"]',
    sheetPosition: 'bottom',
  },
  'gadget-users-role-help': {
    title: 'Read the gadget role guide',
    body: 'This whole role guide stays visible during the inline tutorial. Scroll through it to see gadget-level permissions, then close the role guide when you are ready.',
    progressLabel: 'Step 2 of 2',
    targetSelector: '[data-tutorial-target="role-help-dialog"]',
    actionLabel: 'Close role guide',
    sheetPosition: 'bottom',
  },
  'gadget-components-start': {
    title: 'Create your first component',
    body: 'Open the Create Component card so we can add the first part inside this gadget. A component is one piece of the gadget, like a sensor reading, button, light, or state value.',
    progressLabel: 'Step 1 of 6',
    targetSelector: '[data-tutorial-target="gadget-create-component"]',
    sheetPosition: 'bottom',
  },
  'create-component-name': {
    title: 'Name your component',
    body: 'Type whatever component name you want here, then click Next when you are ready to continue.',
    progressLabel: 'Step 2 of 6',
    targetSelector: '[data-tutorial-target="create-component-name"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'create-component-open-icon-selector': {
    title: 'Open the component icon picker',
    body: 'Click the current component image so we can choose an icon for this build.',
    progressLabel: 'Step 3 of 6',
    targetSelector: '[data-tutorial-target="create-component-icon-preview"]',
    sheetPosition: 'bottom',
  },
  'create-component-upload-custom': {
    title: 'You can upload your own component icon',
    body: 'This upload button lets you use your own image later. Do not tap it in this tutorial step, just use Next so we stay on the guided path.',
    progressLabel: 'Step 4 of 6',
    targetSelector: '[data-tutorial-target="create-component-upload-custom"]',
    actionLabel: 'Next: choose a built-in icon',
    allowTargetInteraction: false,
    sheetPosition: 'bottom',
  },
  'create-component-select-curated': {
    title: 'Pick one of our component icons',
    body: 'Choose any of the built-in icons to continue the tutorial.',
    progressLabel: 'Step 5 of 6',
    targetSelector: '[data-tutorial-target="create-component-curated-icons"]',
    sheetPosition: 'top',
  },
  'create-component-finish': {
    title: 'Finish creating your component',
    body: 'Tap Finish to create the component.',
    progressLabel: 'Step 6 of 6',
    targetSelector: '[data-tutorial-target="create-component-finish"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-needs-gadget': {
    title: 'The dashboard tutorial needs a gadget first',
    body: 'Dashboard components live inside gadgets, so this tutorial cannot continue until the lab has a gadget you can build in. Use the button below to jump into Create Gadget, and we will guide you into Create Component right after that.',
    progressLabel: 'Step 1 of 1',
    targetSelector: '[data-tutorial-target="dashboard-start-tutorial"]',
    actionLabel: 'Go to Create Gadget Tutorial',
    allowTargetInteraction: false,
    sheetPosition: 'bottom',
  },
  'dashboard-controller-needs-component': {
    title: 'The dashboard tutorial needs a component first',
    body: 'Controllers are built around components, so this tutorial cannot continue until the lab has at least one component you can access from a gadget you can edit. Use the button below to jump into Create Component, and we will bring you back here afterward.',
    progressLabel: 'Step 1 of 1',
    targetSelector: '[data-tutorial-target="dashboard-start-tutorial"]',
    actionLabel: 'Go to Create Component Tutorial',
    allowTargetInteraction: false,
    sheetPosition: 'bottom',
  },
  'dashboard-controller-create': {
    title: 'Create a dashboard controller',
    body: 'Tap + to create a controller. Controllers turn Brilliant Labs components into live text readouts, buttons, sliders, gauges, graphs, warnings, and other dashboard surfaces.',
    progressLabel: 'Step 1 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-add"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-name': {
    title: 'Name your controller',
    body: 'Type whatever controller name you want here, then click Next when you are ready to continue.',
    progressLabel: 'Step 2 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-name"]',
    actionLabel: 'Next',
    targetBorderRadius: '10px',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-finish': {
    title: 'Finish creating your controller',
    body: 'Tap Finish to create the controller shell we will use for the rest of the tutorial.',
    progressLabel: 'Step 3 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-finish"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-open-editor': {
    title: 'Open the controller editor',
    body: 'Tap the menu icon to open the editor panel. From here you can add Text, Button, and Analog inputs to build out this controller.',
    progressLabel: 'Step 4 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-open-editor"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-add-text': {
    title: 'Add a Text input',
    body: 'Text inputs are the live readout. Add this first so it can show the test component value while you build the rest of the controller.',
    progressLabel: 'Step 5 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-text"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-add-button': {
    title: 'Add a Button input',
    body: 'Button inputs are for direct actions like start, stop, pulse, or other tap-to-send controls. Tap Button to add one.',
    progressLabel: 'Step 9 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-button"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-add-graph': {
    title: 'Add a Graph input',
    body: 'Graph inputs are for watching changing values over time. Tap Graph to add one to this controller.',
    progressLabel: 'Step 7 of 32',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-graph"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-add-analog': {
    title: 'Add an Analog input',
    body: 'Analog inputs act like a virtual stick for directional or continuous control. Tap Analog to add one.',
    progressLabel: 'Step 15 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-analog"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-add-slider': {
    title: 'Add a Slider input',
    body: 'Slider inputs are good for ranges like speed, brightness, or volume. Tap Slider to add one.',
    progressLabel: 'Step 9 of 32',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-slider"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-add-toggle': {
    title: 'Add a Toggle input',
    body: 'Toggle inputs are for clear on and off control. Tap Toggle to add one.',
    progressLabel: 'Step 10 of 32',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-toggle"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-add-gauge': {
    title: 'Add a Gauge input',
    body: 'Gauge inputs are for reading live values like temperature, power, or pressure. Tap Gauge to add one.',
    progressLabel: 'Step 11 of 32',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-gauge"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-add-warning': {
    title: 'Add a Warning input',
    body: 'Warning inputs surface safe, warning, and danger ranges right on the dashboard. Tap Warning to add one.',
    progressLabel: 'Step 12 of 32',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-warning"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-add-message': {
    title: 'Add a Message input',
    body: 'Message inputs are for sending text or numeric values back to a component. Tap Message to add one.',
    progressLabel: 'Step 13 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-message"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-select-text': {
    title: 'Click the Text card on the dashboard',
    body: 'The Text card is your live readout. Click it in the dashboard so we can set it up as the live test display.',
    progressLabel: 'Step 6 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-text"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-open-text-input-tab': {
    title: 'Open the Input tab',
    body: 'Use the Input tab to edit the selected Text card settings.',
    progressLabel: 'Step 7 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-tab"]',
    targetBorderRadius: '0 5px 5px 0',
    sheetPosition: 'top',
  },
  'dashboard-controller-connect-text': {
    title: 'Wire Text to a test component',
    body: 'Choose a component you can edit here. The Button and Analog inputs will connect to this same test component so the Text card can show the changes live.',
    progressLabel: 'Step 8 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-connect-text"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-select-button': {
    title: 'Click the Button card on the dashboard',
    body: 'Buttons are for one-tap actions. Click the Button card so we can set up its test behavior.',
    progressLabel: 'Step 10 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-button"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-open-button-input-tab': {
    title: 'Open the Input tab',
    body: 'Open the Input tab for the Button so you can choose what it controls.',
    progressLabel: 'Step 11 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-tab"]',
    targetBorderRadius: '0 5px 5px 0',
    sheetPosition: 'top',
  },
  'dashboard-controller-connect-button': {
    title: 'Choose the Button component',
    body: 'Choose the same test component you used for Text so the Button can update that live readout.',
    progressLabel: 'Step 12 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-connect-button"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-button-mode': {
    title: 'Choose how the Button should behave',
    body: 'Push sends the on value while pressed and the off value when released. Toggle flips between off and on each time you tap it. Pick whichever feels right for this controller, then tap Next.',
    progressLabel: 'Step 13 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-button-mode"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'dashboard-controller-button-values': {
    title: 'Set the Button off and on values',
    body: 'Now set the off and on values to whatever you want the Button to send. Tap Next when you are happy with them.',
    progressLabel: 'Step 14 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-button-values"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'dashboard-controller-select-graph': {
    title: 'Click the Graph card on the dashboard',
    body: 'Graphs are for watching how a value changes over time. Click the Graph card so we can show its source component.',
    progressLabel: 'Step 18 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-graph"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-connect-graph': {
    title: 'This is where the Graph card reads from',
    body: 'This selector chooses the component whose history the Graph card shows. Tap Next when you are ready for Analog.',
    progressLabel: 'Step 19 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-connect-graph"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'dashboard-controller-select-analog': {
    title: 'Click the Analog card on the dashboard',
    body: 'Analog acts like a virtual stick for continuous control. Click the Analog card so we can set up its test behavior.',
    progressLabel: 'Step 16 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-analog"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-open-analog-input-tab': {
    title: 'Open the Input tab',
    body: 'Open the Input tab for the Analog input so you can choose what it controls.',
    progressLabel: 'Step 17 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-input-tab"]',
    targetBorderRadius: '0 5px 5px 0',
    sheetPosition: 'top',
  },
  'dashboard-controller-connect-analog': {
    title: 'Choose the Analog component',
    body: 'Choose the same test component you used for Text so the Analog stick updates that live readout too.',
    progressLabel: 'Step 18 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-connect-analog"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-analog-values': {
    title: 'Set the Analog min and max values',
    body: 'Now set the Analog min and max values to whatever you want the stick to send. Tap Next when you are happy with them.',
    progressLabel: 'Step 19 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-analog-values"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'dashboard-controller-select-slider': {
    title: 'Click the Slider card on the dashboard',
    body: 'Sliders are great for ranges like brightness, speed, or volume. Click the Slider card so we can show its connection.',
    progressLabel: 'Step 22 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-slider"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-connect-slider': {
    title: 'This is where the Slider card sends to',
    body: 'This selector chooses which component the Slider updates. Tap Next when you are ready for Toggle.',
    progressLabel: 'Step 23 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-connect-slider"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'dashboard-controller-select-toggle': {
    title: 'Click the Toggle card on the dashboard',
    body: 'Toggles are for clean on and off control. Click the Toggle card so we can show its connection.',
    progressLabel: 'Step 24 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-toggle"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-connect-toggle': {
    title: 'This is where the Toggle card sends to',
    body: 'This selector chooses which component the Toggle updates. Tap Next when you are ready for Gauge.',
    progressLabel: 'Step 25 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-connect-toggle"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'dashboard-controller-select-gauge': {
    title: 'Click the Gauge card on the dashboard',
    body: 'Gauges are for live readings like temperature, power, or pressure. Click the Gauge card so we can show its source component.',
    progressLabel: 'Step 26 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-gauge"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-connect-gauge': {
    title: 'This is where the Gauge card reads from',
    body: 'This selector chooses the component the Gauge displays. Tap Next when you are ready for Warning.',
    progressLabel: 'Step 27 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-connect-gauge"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'dashboard-controller-select-warning': {
    title: 'Click the Warning card on the dashboard',
    body: 'Warnings turn component ranges into clear safe, warning, and danger states. Click the Warning card so we can show its source component.',
    progressLabel: 'Step 28 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-warning"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-connect-warning': {
    title: 'This is where the Warning card reads from',
    body: 'This selector chooses the component whose value drives the Warning ranges. Tap Next when you are ready for Message.',
    progressLabel: 'Step 29 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-connect-warning"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'dashboard-controller-select-message': {
    title: 'Click the Message card on the dashboard',
    body: 'Message cards send text or numeric values back to a component. Click the Message card so we can show its connection.',
    progressLabel: 'Step 30 of 30',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-message"]',
    sheetPosition: 'bottom',
  },
  'dashboard-controller-connect-message': {
    title: 'This is where the Message card sends to',
    body: 'This selector chooses which component the Message card writes back to. Tap Next and then save the controller.',
    progressLabel: 'Step 31 of 31',
    targetSelector: '[data-tutorial-target="dashboard-controller-connect-message"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'dashboard-controller-save': {
    title: 'Save the dashboard controller',
    body: 'Tap Save to keep this tutorial controller and the Text, Button, and Analog inputs you just wired up.',
    progressLabel: 'Step 20 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-save"]',
    sheetPosition: 'top',
  },
  'dashboard-controller-try-button': {
    title: 'Press the Button and watch the Text card change',
    body: 'Now that the controller is saved, press the Button input and watch the Text card update with the same component value. Tap Next when you are done playing with it.',
    progressLabel: 'Step 21 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-button"]',
    actionLabel: 'Next',
    showShade: false,
    sheetPosition: 'top',
  },
  'dashboard-controller-try-analog': {
    title: 'Move the Analog stick and watch the Text card change',
    body: 'Use the Analog stick now and watch the Text card respond the same way. After this, you can explore the rest of the controller inputs on your own.',
    progressLabel: 'Step 22 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-card-analog"]',
    showShade: false,
    sheetPosition: 'top',
  },
  'dashboard-controller-quick-mode': {
    title: 'Quick Mode explained',
    body: 'Quick Mode sends data faster to bBoards but updates are not reflected in the UI. Tap the ? icon for more info.',
    progressLabel: 'Step 23 of 23',
    targetSelector: '[data-tutorial-target="dashboard-controller-quick-mode"]',
    actionLabel: 'Finish',
    sheetPosition: 'top',
  },
  'component-guide-edit': {
    title: 'Open component editing',
    body: 'Tap edit so we can walk through what each part of this component is used for.',
    progressLabel: 'Step 1 of 19',
    targetSelector: '[data-tutorial-target="component-guide-edit"]',
    sheetPosition: 'bottom',
  },
  'component-guide-data-type': {
    title: 'Data Type defines what kind of values fit here',
    body: 'Use Number for things like temperature, brightness, or counters. Use String for text-style states or labels.',
    progressLabel: 'Step 2 of 19',
    targetSelector: '[data-tutorial-target="component-guide-data-type"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'component-guide-current-value': {
    title: 'Current Value is the live state',
    body: 'This is the value the component is holding right now, like its current reading or status. It is what Brilliant Labs automations and MQTT messages work from.',
    progressLabel: 'Step 3 of 19',
    targetSelector: '[data-tutorial-target="component-guide-current-value"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'component-guide-mqtt-topic': {
    title: 'MQTT Topic is the device path',
    body: 'MQTT is the message system many devices use to send and receive updates. This topic is the address or path your hardware listens to or publishes on inside the lab namespace, so Brilliant Labs defaults still stay compatible with general MQTT devices.',
    progressLabel: 'Step 4 of 19',
    targetSelector: '[data-tutorial-target="component-guide-mqtt-topic"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'component-guide-value-triggers': {
    title: 'Value Change Triggers react when this component changes',
    body: 'Use this section when you want this component to start an automation rule after it hits a value, range, on or off state, or just changes at all.',
    progressLabel: 'Step 5 of 19',
    targetSelector: '[data-tutorial-target="component-guide-value-triggers"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'component-guide-value-trigger-add': {
    title: 'Create a value change trigger',
    body: 'Tap + to add an example trigger rule.',
    progressLabel: 'Step 6 of 19',
    targetSelector: '[data-tutorial-target="component-guide-value-trigger-add"]',
    sheetPosition: 'top',
  },
  'component-guide-value-trigger-condition': {
    title: 'This condition is the “if value...” rule',
    body: 'Here you decide what should count as a match, like equal to, between values, on or off, or simply any change.',
    progressLabel: 'Step 7 of 19',
    targetSelector: '[data-tutorial-target="component-guide-value-trigger-condition"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'component-guide-value-trigger-effect-add': {
    title: 'Now add a trigger effect',
    body: 'Trigger effects define what happens when the rule matches. Tap + to add one.',
    progressLabel: 'Step 8 of 19',
    targetSelector: '[data-tutorial-target="component-guide-value-trigger-effect-add"]',
    sheetPosition: 'top',
  },
  'component-guide-value-trigger-effect-type': {
    title: 'Effect Type chooses the action',
    body: 'A trigger can either change another component value or send a push notification.',
    progressLabel: 'Step 9 of 19',
    targetSelector: '[data-tutorial-target="component-guide-value-trigger-effect-type"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'component-guide-value-trigger-effect-change-value': {
    title: 'Change Component Value updates another component',
    body: 'Pick the affected component on the left, then set the new value on the right. This is the core building block for automations.',
    progressLabel: 'Step 10 of 19',
    targetSelector: '[data-tutorial-target="component-guide-value-trigger-effect-change-value"]',
    actionLabel: 'Show push notification option',
    sheetPosition: 'top',
  },
  'component-guide-value-trigger-effect-push': {
    title: 'Push Notification sends a message',
    body: 'Write a push message here. Use {componentName} and {componentValue} for live values.',
    progressLabel: 'Step 11 of 19',
    targetSelector: '[data-tutorial-target="component-guide-value-trigger-effect-push"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'component-guide-schedules': {
    title: 'Scheduled Events run on a clock',
    body: 'Use schedules when you want this component to trigger actions at specific times, instead of waiting for a live value change from the device.',
    progressLabel: 'Step 12 of 19',
    targetSelector: '[data-tutorial-target="component-guide-schedules"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'component-guide-schedule-add': {
    title: 'Create a scheduled event',
    body: 'Tap + to add an example schedule.',
    progressLabel: 'Step 13 of 19',
    targetSelector: '[data-tutorial-target="component-guide-schedule-add"]',
    sheetPosition: 'top',
  },
  'component-guide-schedule-frequency': {
    title: 'Frequency controls how often the schedule repeats',
    body: 'Set the amount, then choose Minutes, Hours, Days, Weeks, or Months depending on how often this automation should run.',
    progressLabel: 'Step 14 of 19',
    targetSelector: '[data-tutorial-target="component-guide-schedule-frequency"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'component-guide-schedule-next-date': {
    title: 'Next Date sets the first run time',
    body: 'This is the date and time the schedule will fire next, and later runs are calculated from it.',
    progressLabel: 'Step 15 of 19',
    targetSelector: '[data-tutorial-target="component-guide-schedule-next-date"]',
    actionLabel: 'Show weekly options',
    sheetPosition: 'top',
  },
  'component-guide-schedule-weeks': {
    title: 'Weekly schedules let you choose weekdays',
    body: 'Use these day buttons when a schedule should only run on selected days of the week.',
    progressLabel: 'Step 16 of 19',
    targetSelector: '[data-tutorial-target="component-guide-schedule-weeks"]',
    actionLabel: 'Show monthly options',
    sheetPosition: 'top',
  },
  'component-guide-schedule-months': {
    title: 'Monthly schedules let you choose calendar days',
    body: 'Pick specific days of the month here, and use the last-day option to handle shorter months cleanly.',
    progressLabel: 'Step 17 of 19',
    targetSelector: '[data-tutorial-target="component-guide-schedule-months"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'component-guide-schedule-effects': {
    title: 'Schedules use the same trigger effects section',
    body: 'Once the schedule fires, these effects can change component values or send push notifications just like value-change triggers do.',
    progressLabel: 'Step 18 of 19',
    targetSelector: '[data-tutorial-target="component-guide-schedule-effects"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'component-guide-finish': {
    title: 'Leave edit mode when you are done',
    body: 'Tap Cancel to exit without saving these tutorial-only example rules.',
    progressLabel: 'Step 19 of 19',
    targetSelector: '[data-tutorial-target="component-guide-finish"]',
    sheetPosition: 'top',
  },
  'contacts-start': {
    title: 'Open your contacts',
    body: 'Click the Contacts icon in the navbar so we can manage your Brilliant Labs connections.',
    progressLabel: 'Getting Started',
    targetSelector: '[data-tutorial-target="navbar-contacts"]',
    sheetPosition: 'bottom',
  },
  'contacts-add-trigger': {
    title: 'Add a new contact',
    body: 'Tap + to start adding a new Brilliant Labs connection.',
    progressLabel: 'Step 1 of 7',
    targetSelector: '[data-tutorial-target="contacts-add-btn"]',
    sheetPosition: 'bottom',
  },
  'contact-info-nickname': {
    title: 'Give them a nickname',
    body: 'Type a name for this contact so you can find them easily in your list later.',
    progressLabel: 'Step 2 of 7',
    targetSelector: '[data-tutorial-target="contact-info-nickname"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'contact-info-method': {
    title: 'Choose the contact method',
    body: 'You can add someone by their Email or their unique Friend Code. Pick which method you want to use.',
    progressLabel: 'Step 3 of 7',
    targetSelector: '[data-tutorial-target="contact-info-method"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'contact-info-email': {
    title: 'Enter their email',
    body: 'Type the email address of the person you want to add.',
    progressLabel: 'Step 4 of 7',
    targetSelector: '[data-tutorial-target="contact-info-email"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'contact-info-code': {
    title: 'Enter their Friend Code',
    body: 'Type the unique Friend Code of the person you want to add.',
    progressLabel: 'Step 4 of 7',
    targetSelector: '[data-tutorial-target="contact-info-code"]',
    actionLabel: 'Next',
    sheetPosition: 'bottom',
  },
  'contact-info-finish': {
    title: 'Send the request',
    body: 'Tap Send Contact Request to finish adding this connection.',
    progressLabel: 'Step 5 of 7',
    targetSelector: '[data-tutorial-target="contact-info-finish"]',
    actionLabel: 'Next',
    sheetPosition: 'top',
  },
  'navbar-profile-trigger': {
    title: 'Where is my Friend Code?',
    body: 'Click the b.Board logo in the top left to open your profile settings.',
    progressLabel: 'Step 6 of 7',
    targetSelector: '[data-tutorial-target="navbar-profile"]',
    sheetPosition: 'bottom',
  },
  'user-settings-friend-code': {
    title: 'This is your Friend Code',
    body: 'Share this code with your friends so they can add you to their labs.',
    progressLabel: 'Step 7 of 7',
    targetSelector: '[data-tutorial-target="user-settings-friend-code"]',
    sheetPosition: 'top',
  },
};
